import { useCallback } from 'react';
import useFeatureToggle from 'framework/featureToggle';
import { Features } from 'infrastructure/featureToggle';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ACCESS_INFO_ACTIONS,
  ACCESS_INFO_SECTIONS,
  ENGAGEMENT_TYPE,
  useProjectSecurityContext,
  useNewProjectUrl,
  useProjectDataContext,
} from 'domain/shared/newProject';
import { TabItem, TabsWrapper } from 'framework/ui/TabsWrapper';

const useProjectTabs = (): TabItem[] => {
  const navigate = useNavigate();
  const {
    getControlsUrl,
    getOutcomesUrl,
    getRisksUrl,
    getFinanceUrl,
    getEVMUrl,
    getCustomerReportUrl,
    getSDLCUrl,
    getProjectTeamUrl,
    getAssumptionsUrl,
    getAcceptanceUrl,
    getProjectTimelineUrl,
    getStakeholdersUrl,
    getEngineeringUrl,
    getProjectClientPortalUrl,
  } = useNewProjectUrl();
  const { data } = useProjectDataContext();
  const { isAccessGranted } = useProjectSecurityContext();
  const { isFeatureEnabled } = useFeatureToggle();
  const onChangeTabCallback = useCallback(
    (route: string) => {
      navigate(route, { preventScrollReset: true });
    },
    [navigate],
  );

  return [
    {
      label: 'Control Planes',
      route: getControlsUrl(data.id),
      isVisible:
        isFeatureEnabled(Features.CONTROLS) &&
        isAccessGranted(ACCESS_INFO_SECTIONS.CONTROL_PLANE, ACCESS_INFO_ACTIONS.VIEW),
    },
    {
      label: 'Outcomes',
      route: getOutcomesUrl(data.id),
      isVisible:
        isAccessGranted(ACCESS_INFO_SECTIONS.BUSINESS_OUTCOME, ACCESS_INFO_ACTIONS.VIEW) ||
        isAccessGranted(ACCESS_INFO_SECTIONS.MILESTONE, ACCESS_INFO_ACTIONS.VIEW),
    },
    {
      label: 'Finance',
      route: getFinanceUrl(data.id),
      isVisible:
        isAccessGranted(ACCESS_INFO_SECTIONS.BUDGET, ACCESS_INFO_ACTIONS.VIEW) ||
        isAccessGranted(ACCESS_INFO_SECTIONS.CHANGE_REQUEST, ACCESS_INFO_ACTIONS.VIEW) ||
        isAccessGranted(ACCESS_INFO_SECTIONS.WASTE, ACCESS_INFO_ACTIONS.VIEW),
    },
    {
      label: 'Risks',
      route: getRisksUrl(data.id),
      isVisible: isAccessGranted(ACCESS_INFO_SECTIONS.RISK, ACCESS_INFO_ACTIONS.VIEW),
    },
    {
      label: 'EVM',
      route: getEVMUrl(data.id),
      isVisible:
        isFeatureEnabled(Features.NEW_PROJECT_EVM) &&
        isAccessGranted(ACCESS_INFO_SECTIONS.EVM, ACCESS_INFO_ACTIONS.VIEW) &&
        data?.settings?.forecastSettings?.isEvmTrackingEnabled === true,
    },
    {
      label: 'Customer Report',
      route: getCustomerReportUrl(data.id),
      isVisible:
        isFeatureEnabled(Features.NEW_PROJECT_CUSTOMER_REPORT) &&
        isAccessGranted(ACCESS_INFO_SECTIONS.CUSTOMER_REPORT, ACCESS_INFO_ACTIONS.VIEW),
    },
    {
      label: 'SDLC',
      route: getSDLCUrl(data.id),
      isVisible:
        isFeatureEnabled(Features.NEW_PROJECT_SDLC) &&
        isAccessGranted(ACCESS_INFO_SECTIONS.SDLC, ACCESS_INFO_ACTIONS.VIEW),
    },
    {
      label: 'Engineering',
      route: getEngineeringUrl(data.id),
      isVisible:
        isFeatureEnabled(Features.NEW_PROJECT_ENGINEERING) &&
        isAccessGranted(ACCESS_INFO_SECTIONS.ENGINEERING, ACCESS_INFO_ACTIONS.VIEW),
    },
    {
      label: 'Team',
      route: getProjectTeamUrl(data.id),
      isVisible:
        isFeatureEnabled(Features.PROJECT_TEAM_TAB) &&
        (isAccessGranted(ACCESS_INFO_SECTIONS.TEAM, ACCESS_INFO_ACTIONS.VIEW) ||
          isAccessGranted(ACCESS_INFO_SECTIONS.CRITICAL_PERIOD, ACCESS_INFO_ACTIONS.VIEW) ||
          isAccessGranted(ACCESS_INFO_SECTIONS.UPCOMING_CHANGES, ACCESS_INFO_ACTIONS.VIEW)),
    },
    {
      label: 'Assumptions',
      route: getAssumptionsUrl(data.id),
      isVisible:
        isFeatureEnabled(Features.NEW_PROJECT_ASSUMPTIONS) &&
        isAccessGranted(ACCESS_INFO_SECTIONS.ASSUMPTION, ACCESS_INFO_ACTIONS.VIEW),
    },
    {
      label: 'Acceptance',
      route: getAcceptanceUrl(data.id),
      isVisible:
        isFeatureEnabled(Features.NEW_PROJECT_ACCEPTANCE) &&
        data?.projectDimension?.engagementType === ENGAGEMENT_TYPE.SCOPE_BASED &&
        isAccessGranted(ACCESS_INFO_SECTIONS.CLIENT_CONFIRMATION, ACCESS_INFO_ACTIONS.VIEW),
    },
    {
      label: 'Stakeholders',
      route: getStakeholdersUrl(data.id),
      isVisible:
        isFeatureEnabled(Features.NEW_PROJECT_STAKEHOLDERS) &&
        isAccessGranted(ACCESS_INFO_SECTIONS.STAKEHOLDER, ACCESS_INFO_ACTIONS.VIEW),
    },
    {
      label: 'Timeline',
      route: getProjectTimelineUrl(data.id),
      isVisible:
        isFeatureEnabled(Features.NEW_PROJECT_TIMELINE) &&
        isAccessGranted(ACCESS_INFO_SECTIONS.PROJECT_TIMELINE_ENTRY, ACCESS_INFO_ACTIONS.VIEW),
    },
    {
      label: 'Client Portal',
      route: getProjectClientPortalUrl(data.id),
      isVisible:
        isFeatureEnabled(Features.CLIENT_PORTAL) &&
        isAccessGranted(ACCESS_INFO_SECTIONS.CLIENT_PORTAL, ACCESS_INFO_ACTIONS.VIEW) &&
        data?.accountData?.isPortalEnabled === true,
    },
  ].map(item => {
    return {
      onClick: () => onChangeTabCallback(item.route),
      value: item.route,
      ...item,
    };
  });
};

const ProjectTabsMenu = () => {
  const location = useLocation();
  const options = useProjectTabs();

  const currentTab = options.find(({ value }) => location.pathname.includes(String(value)))?.value;

  const getTabByLabel = (label: string) => options.find(option => option.label === label)?.value;

  const fallbackTab = getTabByLabel('Outcomes & Risks');

  const defaultTab = currentTab ?? fallbackTab;

  return (
    <TabsWrapper
      defaultTab={defaultTab}
      sxContentProps={{}}
      isShowContent={false}
      sxTabs={{
        overflow: 'auto',
        minHeight: 'auto',
      }}
      sxTab={{
        minHeight: '40px',
        minWidth: 'auto',
        whiteSpace: 'nowrap',
        padding: '12px 8px',
        overflow: 'visible',
      }}
      tabs={options}
    />
  );
};

export default ProjectTabsMenu;
